export interface ISessionPermission {
  permission: string;
  /** [] = print-house-wide / print house without lines of business */
  lineOfBusinessesIds: string[];
}

const toIds = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (id): id is string => typeof id === "string" && id.trim().length > 0,
  );
};

/**
 * Single entry point for session permissions. Accepts the legacy `string[]`,
 * the new `{ permission, lineOfBusinessesIds }[]`, or a mix of both. Drops
 * empty/invalid entries and dedupes by `permission`, merging line ids.
 * A print-house-wide entry (`[]`) wins over line-scoped entries of the same permission.
 */
export const normalizeSessionPermissions = (
  raw: unknown,
): ISessionPermission[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const byName = new Map<string, { ids: Set<string>; wide: boolean }>();
  raw.forEach((item: unknown) => {
    let name: unknown;
    let ids: string[] = [];
    if (typeof item === "string") {
      name = item;
    } else if (item && typeof item === "object") {
      const entry = item as { permission?: unknown; lineOfBusinessesIds?: unknown };
      name = entry.permission;
      ids = toIds(entry.lineOfBusinessesIds);
    }
    if (typeof name !== "string" || name.trim().length === 0) {
      return;
    }
    const current = byName.get(name) ?? { ids: new Set<string>(), wide: false };
    if (ids.length === 0) {
      current.wide = true;
    }
    ids.forEach((id) => current.ids.add(id));
    byName.set(name, current);
  });
  return Array.from(byName.entries()).map(([permission, { ids, wide }]) => ({
    permission,
    lineOfBusinessesIds: wide ? [] : Array.from(ids),
  }));
};

export const getPermissionNames = (
  permissions: ISessionPermission[] | null | undefined,
): string[] => (Array.isArray(permissions) ? permissions.map((p) => p.permission) : []);

/**
 * True when the user has `name`. With `lineOfBusinessId`, the grant must also
 * cover that line (its ids include it, or are empty = print-house-wide).
 */
export const hasPermission = (
  permissions: ISessionPermission[] | null | undefined,
  name: string,
  lineOfBusinessId?: string,
): boolean => {
  if (!Array.isArray(permissions)) {
    return false;
  }
  return permissions.some((entry) => {
    if (entry?.permission !== name) {
      return false;
    }
    if (!lineOfBusinessId) {
      return true;
    }
    const ids = entry.lineOfBusinessesIds ?? [];
    return ids.length === 0 || ids.includes(lineOfBusinessId);
  });
};

export interface ISessionLineSelection {
  id: string;
  isSelected: boolean;
}

/**
 * Permissions that apply to the lines currently selected in the header.
 * - No lines (print house without lines of business) or no selection: unchanged.
 * - Print-house-wide entries (`[]`) are always kept.
 * - Line-scoped entries keep only the selected line ids and are dropped when
 *   none of their lines is selected (a permission applies if ANY selected line grants it).
 */
export const getEffectivePermissions = (
  permissions: ISessionPermission[] | null | undefined,
  lines: ISessionLineSelection[] | null | undefined,
): ISessionPermission[] => {
  if (!Array.isArray(permissions)) {
    return [];
  }
  if (!Array.isArray(lines) || lines.length === 0) {
    return permissions;
  }
  const selected = new Set(
    lines.filter((line) => line?.isSelected).map((line) => line.id),
  );
  if (selected.size === 0) {
    return permissions;
  }
  const result: ISessionPermission[] = [];
  permissions.forEach((entry) => {
    const ids = entry.lineOfBusinessesIds ?? [];
    if (ids.length === 0) {
      result.push(entry);
      return;
    }
    const selectedIds = ids.filter((id) => selected.has(id));
    if (selectedIds.length > 0) {
      result.push({ ...entry, lineOfBusinessesIds: selectedIds });
    }
  });
  return result;
};
